import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMutation } from '@tanstack/react-query';

import Button from '../../../components/Library/Button';
import Input from '../../../components/Library/Input';
import Label from '../../../components/Library/Label';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '../../../components/Library/Modal';
import Select from '../../../components/Library/Select';
import InlineLoadingSpinner from '../../../components/LoadingSpinner/InlineLoadingSpinner';
import { snakeCaseToWords } from '../../../libs/libString';
import {
  ScimIdp, ScimIntegrationWithToken, useSCIM_IntegrationsApi
} from '../../../middle_auth_api';
import { ScimIdpEnum } from '../../../types/ScimIdpEnum';

const ScimIntegrationsNew: React.FC = () => {
  const scimIntegrationsApi = useSCIM_IntegrationsApi();
  const navigate = useNavigate();

  const [newIntegration, setNewIntegration] = useState<ScimIntegrationWithToken | null>(null);

  const [name, setName] = useState('');
  const [idpHint, setIdpHint] = useState<ScimIdp>();
  const [idpOtherName, setIdpOtherName] = useState('');

  const saveMutation = useMutation(async () => {
    const integration = await scimIntegrationsApi.create_scim_integration({
      name: name,
      idp_hint: idpHint!,
      idp_other_name: (idpHint == 'other' ? idpOtherName : null)
    });

    return integration;
  }, {
    onSuccess: (data) => {
      setNewIntegration(data);
    }
  });

  const saveNew = (): void => {
    saveMutation.mutate();
  };

  const closeModal = (): void => {
    navigate('..')
  };

  return <>
    <Modal show={true} closeModal={closeModal}>
      <ModalHeader closeModal={closeModal}>Create New SCIM Integration</ModalHeader>
      <ModalBody>
        <div className='p-4'>
          {saveMutation.isLoading ? <>
            <InlineLoadingSpinner />
          </> : <>
            <div>
              <Label htmlFor='name'>Name</Label>
              <Input id='name' readOnly={newIntegration != null} value={name} onChange={(e) => setName(e.target.value)} className='w-full' />
            </div>
            <div>
              <Label htmlFor='idpHint'>IdP</Label>
              <Select id='idpHint' className='block w-full' value={idpHint} onChange={(e) => setIdpHint(e.target.value as ScimIdp)}>
                <option value={''} disabled>Choose Your IdP</option>
                {Object.keys(ScimIdpEnum).map((idp) => (
                  <option key={idp} value={idp}>{snakeCaseToWords(idp)}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor='idpOtherName'>IdP Name</Label>
              <Input id='idpOtherName' readOnly={newIntegration != null} disabled={idpHint != "other"} value={idpOtherName} onChange={(e) => setIdpOtherName(e.target.value)} className='w-full' />
            </div>
            {newIntegration != null && <div>
              <Label htmlFor='token'>Token</Label>
              <Input id='token' readOnly={true} value={newIntegration.token} className='w-full' />
            </div>}
          </>}
        </div>
      </ModalBody>
      {newIntegration == null && <ModalFooter>
        <Button variant='primary' type='submit' onClick={saveNew}>Save</Button>
        <Button variant='danger' type='reset' onClick={closeModal}>Cancel</Button>
      </ModalFooter>}
    </Modal>
  </>;
}

export default ScimIntegrationsNew;